/**
 * HexTooltip component - displays hex information on hover.
 * Shows hex name, terrain, owner, and coordinates.
 * Positioned near the cursor/hex location.
 *
 * Claimed hexes use a dark slate theme.
 * Unclaimed/claimable hexes use a medieval parchment aesthetic.
 */

import type { HexData } from './types';
import type { AxialCoord } from '@/lib/hexMath';

interface Props {
    /** The hex being hovered (null for unclaimed hexes) */
    hex: HexData | null;
    /** The coordinates of the hovered hex */
    coord: AxialCoord;
    /** Screen position for the tooltip (x, y) */
    position: { x: number; y: number };
    /** Whether the tooltip is visible */
    visible: boolean;
    /** Whether this unclaimed hex is claimable (adjacent to a claimed hex) */
    isClaimable: boolean;
    /** Whether the current user can claim hexes at all (not at limit, authenticated) */
    canClaimHexes: boolean;
}

/**
 * Parchment-styled tooltip wrapper.
 * Uses a medieval parchment aesthetic with gradient background.
 */
function ParchmentTooltip({ children }: { children: React.ReactNode }) {
    return (
        <div
            className="relative rounded-lg p-3 min-w-48 max-w-64"
            style={{
                background: 'linear-gradient(135deg, #f5e6d3 0%, #e8d5b7 25%, #f0e2cc 50%, #e5d4b8 75%, #f2e4c9 100%)',
                boxShadow: '0 4px 16px rgba(0,0,0,0.35), inset 0 0 30px rgba(139,90,43,0.1)',
            }}
        >
            {/* Subtle aged paper edge effect at top */}
            <div
                className="absolute top-0 left-0 right-0 h-1 rounded-t-lg pointer-events-none"
                style={{ background: 'linear-gradient(to bottom, rgba(139,90,43,0.15), transparent)' }}
            />
            {/* Subtle aged paper edge effect at bottom */}
            <div
                className="absolute bottom-0 left-0 right-0 h-1 rounded-b-lg pointer-events-none"
                style={{ background: 'linear-gradient(to top, rgba(139,90,43,0.15), transparent)' }}
            />
            {children}
        </div>
    );
}

/**
 * Tooltip that appears when hovering over a hex on the map.
 * Shows different content for claimed vs unclaimed hexes.
 * For unclaimed hexes, shows whether it's claimable (adjacent to claimed hex).
 * For users who cannot claim hexes, shows a neutral message.
 */
export function HexTooltip({ hex, coord, position, visible, isClaimable, canClaimHexes }: Props) {
    if (!visible) return null;

    // Calculate tooltip position with offset to avoid cursor overlap
    // Position tooltip to the right and below the cursor
    const tooltipStyle: React.CSSProperties = {
        position: 'fixed',
        left: position.x + 16,
        top: position.y + 16,
        zIndex: 1000,
        pointerEvents: 'none', // Don't interfere with mouse events
    };

    // A hex is considered "configured but unclaimed" if it exists but has no owner
    // These hexes were set up by a GM for players to claim
    const isConfiguredButUnclaimed = hex && hex.owner === null;

    // Claimed hex with owner - use parchment theme
    if (hex && hex.owner) {
        return (
            <div style={tooltipStyle}>
                <ParchmentTooltip>
                    <div className="space-y-2">
                        <div className="flex items-center justify-between gap-2">
                            <h3 className="text-amber-900 font-serif font-semibold truncate">{hex.name}</h3>
                            <span className="text-xs text-amber-700/70 whitespace-nowrap">
                                ({coord.q}, {coord.r})
                            </span>
                        </div>
                        <div className="text-sm space-y-1">
                            <div className="flex items-center gap-2">
                                <span className="text-amber-800/70">Terrain:</span>
                                <span className="text-amber-900 capitalize">{hex.terrain}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-amber-800/70">Owner:</span>
                                <span className="text-amber-900">{hex.ownerName || hex.owner.username}</span>
                            </div>
                        </div>
                        <div className="text-xs text-amber-700 pt-1 border-t border-amber-600/20 font-medium">
                            ✦ Click to view details
                        </div>
                    </div>
                </ParchmentTooltip>
            </div>
        );
    }

    // Unclaimed hexes (configured or completely unclaimed) - use parchment theme
    return (
        <div style={tooltipStyle}>
            <ParchmentTooltip>
                {isConfiguredButUnclaimed ? (
                    // Configured but unclaimed hex - show hex info with claim prompt
                    <div className="space-y-2">
                        <div className="flex items-center justify-between gap-2">
                            <h3 className="text-amber-900 font-serif font-semibold truncate">{hex.name}</h3>
                            <span className="text-xs text-amber-700/70 whitespace-nowrap">
                                ({coord.q}, {coord.r})
                            </span>
                        </div>
                        <div className="text-sm space-y-1">
                            <div className="flex items-center gap-2">
                                <span className="text-amber-800/70">Terrain:</span>
                                <span className="text-amber-900 capitalize">{hex.terrain}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-amber-800/70">Status:</span>
                                <span className="text-amber-600 font-medium">Available to claim</span>
                            </div>
                        </div>
                        {/* Show claim-related messages */}
                        {canClaimHexes ? (
                            isClaimable ? (
                                <div className="text-xs text-green-700 pt-1 border-t border-amber-600/20 font-medium">
                                    ✦ Click to claim this hex
                                </div>
                            ) : (
                                <div className="text-xs text-red-700 pt-1 border-t border-amber-600/20">
                                    Not adjacent to claimed hexes
                                </div>
                            )
                        ) : (
                            <div className="text-xs text-amber-700/60 pt-1 border-t border-amber-600/20 italic">
                                This territory is available
                            </div>
                        )}
                    </div>
                ) : (
                    // Completely unclaimed hex - no hex data exists
                    <div className="space-y-2">
                        <div className="flex items-center justify-between gap-2">
                            <h3 className="text-amber-800 font-serif font-semibold">Unclaimed Hex</h3>
                            <span className="text-xs text-amber-700/60 whitespace-nowrap">
                                ({coord.q}, {coord.r})
                            </span>
                        </div>
                        {/* Show claim-related messages only if user can claim hexes */}
                        {canClaimHexes ? (
                            isClaimable ? (
                                <div className="text-xs text-green-700 font-medium">
                                    ✦ Click to claim this hex
                                </div>
                            ) : (
                                <div className="text-xs text-red-700">
                                    Not adjacent to claimed hexes
                                </div>
                            )
                        ) : (
                            // User cannot claim hexes - show neutral message
                            <div className="text-xs text-amber-700/60 italic">
                                This territory is unclaimed
                            </div>
                        )}
                    </div>
                )}
            </ParchmentTooltip>
        </div>
    );
}

